/**
 * Mock data generators for User entities.
 *
 * Security Note: Math.random() is used throughout this file for generating
 * test/mock data only. This file is excluded from production bundles and
 * is not used for any security-sensitive operations.
 * All Math.random() usages here are safe (NOSONAR).
 */
import { User } from '../../core/models/user.model';

const firstNames = [
  'John',
  'Jane',
  'Michael',
  'Sarah',
  'David',
  'Emily',
  'Robert',
  'Lisa',
  'William',
  'Jennifer',
  'James',
  'Patricia',
  'Charles',
  'Linda',
  'Thomas',
];
const lastNames = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Wilson',
  'Anderson',
  'Taylor',
  'Thomas',
  'Moore',
];
const roleNames = ['Admin', 'User', 'Viewer', 'Manager', 'Operator'];
const phoneAreaCodes = [
  '212',
  '310',
  '312',
  '415',
  '617',
  '404',
  '305',
  '713',
  '602',
  '503',
];
const domains = ['CORP', 'HQ', 'BRANCH'];

function randomItem<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function generatePhoneNumber(): string | null {
  if (Math.random() > 0.3) {
    const areaCode = randomItem(phoneAreaCodes);
    const exchange = String(Math.floor(Math.random() * 900) + 100);
    const subscriber = String(Math.floor(Math.random() * 9000) + 1000);
    return `+1-${areaCode}-${exchange}-${subscriber}`;
  }
  return null;
}

function generateUsername(
  firstName: string,
  lastName: string,
  index: number
): string {
  return `${firstName.toLowerCase()}.${lastName.toLowerCase()}${index > 10 ? index : ''}`;
}

function generateEmail(firstName: string, lastName: string): string {
  return `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`;
}

function generateArNumber(index: number): string {
  return `AR${String(index).padStart(6, '0')}`;
}

export function generateMockUser(index: number): User {
  const firstName = randomItem(firstNames);
  const lastName = randomItem(lastNames);
  const userName = generateUsername(firstName, lastName, index);
  const domain = randomItem(domains);
  const isActive = index <= 100 ? true : Math.random() > 0.3;

  return {
    id: index,
    userName,
    firstName,
    lastName,
    middleName: Math.random() > 0.7 ? randomItem(firstNames) : null,
    fullName: `${firstName} ${lastName}`,
    email: generateEmail(firstName, lastName),
    workPhoneNumber: generatePhoneNumber(),
    arNumber: generateArNumber(index),
    adDomainName: `${domain}\\${userName}`,
    active: isActive,
    lastSignedOnDate: isActive ? new Date().toISOString() : null,
    createdDate: new Date(
      Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000
    ).toISOString(),
    roleName: randomItem(roleNames),
  };
}

export function generateMockUsers(count = 150): User[] {
  return Array.from({ length: count }, (_, i) => generateMockUser(i + 1));
}

// Pre-generated mock data for consistent testing
let cachedUsers: User[] | null = null;

export function getMockUsers(): User[] {
  cachedUsers ??= generateMockUsers(150);
  return cachedUsers;
}
